import {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {AdjuntosArt16} from '@/components/solicitudes-ips/adjuntos-art16'
import {FichaDatos} from '@/components/solicitudes-ips/ficha-datos'
import {HelpIcon} from '@/components/help-icon'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {useTagPeople} from '@/hooks/empresa/organizacion/use-tags'
import {
  useGenerarLote,
  useIpsList,
  usePrevisualizarSolicitud,
  type CargoListo,
  type LoteSolicitudes,
  type Previsualizacion,
  type AdjuntoArt16,
  type PruebaAutorizada,
  type TipoEvaluacion,
} from '@/hooks/salud/medicina_laboral/use-solicitudes-ips'
import {translateApiError, translateDetail} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

// Todas las casillas de §2, no solo las que Kairos propuso: never-assume prohíbe decidir en silencio.
const TIPOS: TipoEvaluacion[] = [
  'preingreso',
  'periodico',
  'cambio_ocupacion',
  'egreso',
  'post_incapacidad',
  'reintegro',
]

// Marcarlos impide leer la muestra de una persona como si fuera el papel de todas.
const POR_PERSONA = ['trabajador', 'tipo_evaluacion', 'eps', 'afp', 'arl', 'lugar_atencion', 'fecha_estimada_ingreso']

/** Nada se guarda hasta generar; la muestra es de una persona porque los datos y el tipo son por persona. */
export function PrepararDialog({
  businessId,
  cargo,
  onClose,
  onGenerado,
}: {
  businessId: string
  cargo: CargoListo | null
  onClose: () => void
  onGenerado: (lote: LoteSolicitudes) => void
}) {
  const {t} = useTranslation()
  const personas = useTagPeople(businessId, 'positions', cargo?.position_id, 1, cargo !== null)
  const previsualizar = usePrevisualizarSolicitud(businessId)
  const lote = useGenerarLote(businessId)
  const [busquedaIps, setBusquedaIps] = useState('')
  const ips = useIpsList(businessId, {page: 1, search: busquedaIps})
  const [ipsId, setIpsId] = useState<string | null>(null)
  const [vista, setVista] = useState<Previsualizacion | null>(null)
  const [tipo, setTipo] = useState<TipoEvaluacion | null>(null)
  const [tipoMotivo, setTipoMotivo] = useState('')
  const [bateria, setBateria] = useState<PruebaAutorizada[]>([])
  const [adjuntos, setAdjuntos] = useState<AdjuntoArt16[]>([])

  const primera = personas.data?.items[0]
  const sinTitulares = personas.data !== undefined && personas.data.items.length === 0
  const opciones = ips.data?.items ?? []
  const elegida = opciones.find((fila) => fila.id === ipsId) ?? null

  // POST en un efecto: en el render corría dos veces bajo StrictMode y en cada re-render.
  const pedir = previsualizar.mutate
  useEffect(() => {
    if (!cargo || !primera) return
    pedir({employeeId: primera.id, positionId: cargo.position_id}, {onSuccess: setVista})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargo?.position_id, primera?.id])

  // Con una sola IPS, preguntar sería preguntar lo que ya se sabe (ask-least).
  useEffect(() => {
    if (opciones.length === 1 && ipsId === null && busquedaIps === '') setIpsId(opciones[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones.length])

  function cerrar() {
    setVista(null)
    setIpsId(null)
    setTipo(null)
    setTipoMotivo('')
    setBateria([])
    setBusquedaIps('')
    lote.reset()
    onClose()
  }

  const preguntaElTipo = vista?.pregunta_el_tipo ?? false
  const faltaResponder = preguntaElTipo && (tipo === null || tipoMotivo.trim() === '')
  const sinIps = ips.data !== undefined && opciones.length === 0 && busquedaIps === ''
  // Sin `vista` se firmaría sin haber visto nada.
  const puedeGenerar =
    cargo !== null &&
    vista !== null &&
    !sinTitulares &&
    !sinIps &&
    !faltaResponder &&
    !lote.isPending &&
    !lote.isSuccess

  return (
    <Dialog open={cargo !== null} onOpenChange={(abierto) => !abierto && cerrar()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('solicitudIps.preparar.title', {cargo: cargo?.cargo ?? ''})}</DialogTitle>
          <DialogDescription>{t('solicitudIps.preparar.ayuda', {personas: cargo?.personas ?? 0})}</DialogDescription>
        </DialogHeader>

        {sinTitulares && (
          <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="solicitudIps-sin-titulares">
            {t('solicitudIps.cargos.sinTitulares')}
          </p>
        )}

        {previsualizar.isError && (
          <p className="text-sm text-destructive" data-testid="solicitudIps-previsualizacion-error">
            {translateApiError(previsualizar.error)}
          </p>
        )}

        {vista && primera && (
          <div className="space-y-4" data-testid="solicitudIps-previsualizacion">
            <p className="text-sm text-muted-foreground" data-testid="solicitudIps-muestra-de">
              {t('solicitudIps.preparar.muestraDe', {nombre: primera.nombre_completo})}
            </p>
            <div>
              <h3 className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
                {t('solicitudIps.preparar.doceDatos')}
              </h3>
              <div className="mt-2">
                <FichaDatos datos={vista.datos} porPersona={POR_PERSONA} testId="solicitudIps-ficha" />
              </div>
            </div>

            <div>
              <h3 className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
                {t('solicitudIps.preparar.enfasis')}
              </h3>
              {vista.enfasis.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">{t('solicitudIps.preparar.sinEnfasis')}</p>
              ) : (
                <ul className="mt-1 space-y-1 text-sm">
                  {vista.enfasis.map((item) => (
                    <li key={`${item.texto}-${item.numero_fila ?? ''}`}>
                      {item.texto}
                      <span className="text-xs text-muted-foreground">
                        {' — '}
                        {t('solicitudIps.preparar.detalleEnfasis', {fila: item.numero_fila ?? ''})}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {preguntaElTipo && (
          <fieldset className="space-y-2" data-testid="solicitudIps-elegir-tipo">
            <legend className="inline-flex items-center gap-1 text-sm font-medium">
              {t('solicitudIps.preparar.elegirTipo')}
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('solicitudIps.preparar.elegirTipo')})}
                testId="solicitudIps-elegir-tipo-help">
                {t('solicitudIps.preparar.elegirTipoAyuda')}
              </HelpIcon>
            </legend>
            {TIPOS.map((clave) => {
              const candidato = vista?.candidatos.find((c) => c.tipo === clave)
              return (
                <label key={clave} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="solicitud-tipo"
                    value={clave}
                    data-testid={`solicitudIps-tipo-${clave}`}
                    checked={tipo === clave}
                    onChange={() => setTipo(clave)}
                    className="mt-0.5 accent-primary"
                  />
                  <span>
                    {t(`solicitudIps.tipo.${clave}`)}
                    {candidato && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        {t('solicitudIps.preparar.propuestoPorKairos')}
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
            <Textarea
              maxLength={tope('NOTA')}
              data-testid="solicitudIps-tipo-motivo"
              placeholder={t('solicitudIps.preparar.tipoMotivo')}
              value={tipoMotivo}
              onChange={(e) => setTipoMotivo(e.target.value)}
            />
          </fieldset>
        )}

        {/* D-I: la batería que la empresa autoriza y asume, acto de firmante de §4. */}
        <fieldset className="space-y-2" data-testid="solicitudIps-bateria">
          <legend className="text-sm font-medium">{t('solicitudIps.preparar.bateriaTitle')}</legend>
          {bateria.map((prueba, indice) => (
            <div key={indice} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">{prueba.prueba}</span>
              <span className="text-xs text-muted-foreground">{prueba.origen_texto}</span>
              <Button
                size="sm"
                variant="outline"
                data-testid={`solicitudIps-bateria-quitar-${indice}`}
                onClick={() => setBateria(bateria.filter((_, i) => i !== indice))}>
                {t('solicitudIps.preparar.bateriaQuitar')}
              </Button>
            </div>
          ))}
          <BateriaNueva onAgregar={(prueba) => setBateria([...bateria, prueba])} />
          {bateria.length === 0 && (
            <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="solicitudIps-preparar-sin-bateria">
              {t('solicitudIps.preparar.sinBateria')}
            </p>
          )}
        </fieldset>

        {/* Art. 16 lit. c (KAI-154): sin filas o sin Matriz se avisa antes de generar. */}
        {vista && (
          <p
            className={
              vista.matriz_en_kairos && vista.matriz_filas > 0
                ? 'text-xs text-muted-foreground'
                : 'notice-amber rounded-lg px-3 py-2 text-sm'
            }
            data-testid="solicitudIps-preparar-matriz">
            {!vista.matriz_en_kairos
              ? t('solicitudIps.matriz.sinMatriz')
              : vista.matriz_filas === 0
                ? t('solicitudIps.matriz.sinFilas')
                : t('solicitudIps.matriz.enKairos', {count: vista.matriz_filas})}
          </p>
        )}

        {vista && (
          <AdjuntosArt16
            businessId={businessId}
            adjuntos={adjuntos}
            conExcelDeMatriz={!vista.matriz_en_kairos}
            hoy={vista.hoy}
            onChange={setAdjuntos}
          />
        )}

        {sinIps ? (
          <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="solicitudIps-sin-ips">
            {t('solicitudIps.preparar.sinIps')}{' '}
            <Link className="underline" to={`/businesses/${businessId}/ips`}>
              {t('solicitudIps.preparar.registrarIps')}
            </Link>
          </p>
        ) : (
          <div className="space-y-1">
            <span className="text-sm font-medium">{t('solicitudIps.preparar.elegirIps')}</span>
            <EntitySearchSelect
              items={opciones}
              value={elegida}
              getKey={(fila) => fila.id}
              filterText={(fila) => `${fila.name} ${fila.correo_institucional}`}
              renderOption={(fila) => (
                <span>
                  {fila.name}{' '}
                  <span className="font-mono text-xs text-muted-foreground">{fila.correo_institucional}</span>
                </span>
              )}
              renderTriggerValue={(fila) => `${fila.name} · ${fila.correo_institucional}`}
              placeholder={t('solicitudIps.preparar.elegirIpsPlaceholder')}
              searchPlaceholder={t('ips.buscar')}
              noResultsLabel={t('solicitudIps.preparar.ipsSinResultados')}
              testId="solicitudIps-ips"
              onQueryChange={setBusquedaIps}
              onPick={(fila) => setIpsId(fila?.id ?? null)}
            />
          </div>
        )}

        {lote.data && (
          <div className="space-y-1 text-sm" data-testid="solicitudIps-resultado">
            <p>{t('solicitudIps.preparar.generadas', {count: lote.data.generadas.length})}</p>
            {lote.data.no_generadas.map((fila) => (
              <p key={fila.employee_id} className="text-muted-foreground">
                {t('solicitudIps.preparar.noGenerada', {
                  nombre: fila.trabajador ?? '',
                  motivo: translateDetail(fila.motivo),
                })}
              </p>
            ))}
          </div>
        )}

        {lote.isError && <p className="text-sm text-destructive">{translateApiError(lote.error)}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" data-testid="solicitudIps-preparar-cerrar" onClick={cerrar}>
            {t('solicitudIps.preparar.cerrar')}
          </Button>
          {!lote.isSuccess && (
            <Button
              size="sm"
              data-testid="solicitudIps-generar"
              disabled={!puedeGenerar}
              onClick={() =>
                cargo &&
                lote.mutate(
                  {
                    positionId: cargo.position_id,
                    ipsId,
                    tipo: preguntaElTipo ? tipo : null,
                    tipoMotivo: preguntaElTipo ? tipoMotivo.trim() : null,
                    bateria,
                    adjuntos,
                  },
                  {onSuccess: onGenerado}
                )
              }>
              {t('solicitudIps.preparar.generar', {count: cargo?.personas ?? 0})}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Sin el origen escrito, la IPS recibe exámenes sin poder saber por qué. */
function BateriaNueva({onAgregar}: {onAgregar: (prueba: PruebaAutorizada) => void}) {
  const {t} = useTranslation()
  const [prueba, setPrueba] = useState('')
  const [origen, setOrigen] = useState('')

  return (
    <div className="flex flex-wrap items-end gap-2">
      <Input
        maxLength={tope('NOTA')}
        className="max-w-[12rem]"
        data-testid="solicitudIps-bateria-prueba"
        placeholder={t('solicitudIps.preparar.bateriaPrueba')}
        value={prueba}
        onChange={(e) => setPrueba(e.target.value)}
      />
      <Input
        maxLength={tope('NOTA')}
        className="max-w-[14rem]"
        data-testid="solicitudIps-bateria-origen"
        placeholder={t('solicitudIps.preparar.bateriaOrigen')}
        value={origen}
        onChange={(e) => setOrigen(e.target.value)}
      />
      <Button
        size="sm"
        variant="outline"
        data-testid="solicitudIps-bateria-agregar"
        disabled={prueba.trim() === '' || origen.trim() === ''}
        onClick={() => {
          onAgregar({prueba: prueba.trim(), origen_texto: origen.trim()})
          setPrueba('')
          setOrigen('')
        }}>
        {t('solicitudIps.preparar.bateriaAgregar')}
      </Button>
    </div>
  )
}
